import { CurrencyPipe } from '@angular/common';
import {
	Component,
	computed,
	DestroyRef,
	inject,
	type OnInit,
	signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { environment } from '../../../../environments/environment';
import { type SoGapSalesOrder } from '../../models/so-gap-sales-order';
import { SoGapHttpService } from '../../services/so-gap-http.service';
import { ToastService } from '../../services/toast.service';

interface SoGapSalesOrderRow extends SoGapSalesOrder {
	rowKey: string;
}

@Component({
	selector: 'app-so-gap',
	standalone: true,
	imports: [
		FormsModule,
		CurrencyPipe,
		TableModule,
		ButtonModule,
		InputTextModule,
		DialogModule,
		TagModule
	],
	templateUrl: './so-gap.component.html',
	styleUrl: './so-gap.component.sass'
})
export class SoGapComponent implements OnInit {
	protected readonly environment = environment;
	private readonly soGapHttp = inject(SoGapHttpService);
	private readonly toastService = inject(ToastService);
	private readonly destroyRef = inject(DestroyRef);

	protected readonly searchTerm = signal('');
	protected readonly orders = signal<SoGapSalesOrderRow[]>([]);
	protected readonly isLoading = signal(true);
	protected readonly detailOpen = signal(false);
	protected readonly selectedOrder = signal<SoGapSalesOrderRow | null>(null);
	protected readonly isCreatingTasks = signal(false);

	protected readonly filteredOrders = computed(() => {
		const term = this.searchTerm().trim().toLowerCase();
		const orders = this.orders();
		if (!term) return orders;

		return orders.filter((order) =>
			[
				order.ticketNumber,
				order.name,
				order.parentCompany,
				order.nsStatus,
				order.po,
				order.requisitioner,
				order.afe,
				order.costCenter,
				order.location,
				order.jobLocation,
				String(order.internalId)
			].some((value) => value?.toLowerCase().includes(term))
		);
	});

	public ngOnInit(): void {
		this.loadSalesOrders();
	}

	protected loadSalesOrders(): void {
		this.isLoading.set(true);
		this.soGapHttp
			.fetchSalesOrders()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (orders) => {
					this.orders.set(
						orders.map((order, index) => ({
							...order,
							rowKey: `${order.internalId}-${order.lineNumber ?? 'line'}-${index}`
						}))
					);
					this.isLoading.set(false);
				},
				error: () => {
					this.isLoading.set(false);
					this.toastService.showErrorToast(
						'Failed to load sales orders.'
					);
				}
			});
	}

	protected openOrder(order: SoGapSalesOrderRow): void {
		this.selectedOrder.set(order);
		this.detailOpen.set(true);
	}

	protected createTasks(): void {
		const order = this.selectedOrder();
		if (!order || this.isCreatingTasks()) return;

		this.isCreatingTasks.set(true);
		this.soGapHttp
			.createTasks(order)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (updated) => {
					this.isCreatingTasks.set(false);
					this.selectedOrder.set({ ...order, ...updated, rowKey: order.rowKey });
					this.toastService.showSuccessToast(
						`Tasks created for ${updated.ticketNumber}.`
					);
				},
				error: () => {
					this.isCreatingTasks.set(false);
					this.toastService.showErrorToast(
						'Failed to create tasks for this sales order.'
					);
				}
			});
	}
}
